import { useState } from 'react'
import {
  Box,
  Card,
  CardMedia,
  Divider,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { sortBy } from 'ramda'
import { ImageIcon } from 'src/v3/core/icons'
import { Event, Course } from 'src/v3/core/types'
import {
  coursePublicImageSrc,
  courseResizedImageSrc,
} from 'src/v3/self-paced/utils/course-thumbnail'
import LifecycleChip from 'src/v3/content-pipeline/components/lifecycle/lifecycle-chip'
import LifecycleFilter, {
  LifecycleFilterValue,
  filterByLifecycle,
  lifecycleCounts,
} from 'src/v3/content-pipeline/components/lifecycle/lifecycle-filter'

type EventOrCourse = Event | (Course & { sectionName?: string })

type EventListProps = {
  events: EventOrCourse[]
  eventsStatus: 'uninitialized' | 'pending' | 'fulfilled' | 'rejected'
  activeEventId?: string
  onEventSelect: (eventId: string) => void
  listTitle?: string
  showLifecycleFilter?: boolean
}

let EventCard = ({
  event,
  checked,
  thumbnailUrl,
  imageName,
  onSelect,
}: {
  event: EventOrCourse
  checked: boolean
  thumbnailUrl: string | null
  imageName: string | undefined
  onSelect: () => void
}) => {
  // Determine if it's an Event or Course and extract appropriate fields
  let isEvent = 'hours' in event
  let duration = isEvent ? (event as Event).hours : (event as Course).length
  let category = isEvent
    ? (event as Event).categoryName
    : (event as Course & { sectionName?: string }).sectionName ||
      (event as Course).section
  let [imageError, setImageError] = useState(false)

  return (
    <Box role="listitem">
      <Card
        variant="outlined"
        component="button"
        sx={{
          p: 0,
          bgcolor: checked ? 'rgba(25, 118, 210, 0.12)' : undefined,
          '&:hover': {
            cursor: 'pointer',
            bgcolor: checked ? 'rgba(25, 118, 210, 0.12)' : 'divider',
          },
          '&:focus': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
          border: 'none',
          textAlign: 'left',
          width: '100%',
          display: 'flex',
          overflow: 'hidden',
        }}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
          }
        }}
        aria-pressed={checked}
        aria-label={`Select ${event.name} for performance analysis`}
        tabIndex={0}
      >
        {thumbnailUrl && !imageError ? (
          <CardMedia
            component="img"
            image={thumbnailUrl}
            alt={event.name}
            onError={(e) => {
              // Events never have a v1 /public/ counterpart — only Course
              // imageName can legitimately live there — so only Courses get
              // a fallback attempt before falling through to the placeholder.
              let fallback = !isEvent && coursePublicImageSrc(imageName)
              if (fallback && imageName && e.currentTarget.src !== fallback) {
                // eslint-disable-next-line fp/no-mutation
                e.currentTarget.src = fallback
              } else {
                setImageError(true)
              }
            }}
            sx={{
              width: 120,
              height: 80,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : thumbnailUrl ? (
          <Box
            sx={{
              width: 120,
              height: 80,
              flexShrink: 0,
              bgcolor: 'background.paperDark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
          </Box>
        ) : null}
        <Box sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
              }}
            >
              {event.name}
            </Typography>
            {isEvent && (
              <LifecycleChip lifecycleState={(event as Event).lifecycleState} />
            )}
          </Box>
          <Typography color="text.secondary" variant="caption">
            {duration || 0}h{category && ` • ${category}`}
          </Typography>
        </Box>
      </Card>
    </Box>
  )
}

export let EventList = ({
  events,
  eventsStatus,
  activeEventId,
  onEventSelect,
  listTitle = 'Events',
  showLifecycleFilter = false,
}: EventListProps) => {
  let [searchQuery, setSearchQuery] = useState('')
  let [lifecycleFilter, setLifecycleFilter] =
    useState<LifecycleFilterValue>('all')

  // Only Event payloads carry lifecycleState; Course lists skip this filter.
  let lifecycleFilteredEvents = showLifecycleFilter
    ? filterByLifecycle(events, lifecycleFilter)
    : events

  // Filter events based on search query
  let searchFilteredEvents = lifecycleFilteredEvents.filter((event) =>
    event.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  // Sort alphabetically
  let filteredEvents = sortBy(
    (event) => event.name?.toLowerCase() || '',
    searchFilteredEvents,
  )

  return (
    <Paper
      sx={{ p: 3, overflow: 'hidden' }}
      component="nav"
      aria-label="Events list"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h2">
          {listTitle}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TextField
        size="small"
        fullWidth
        placeholder="Search events..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2, bgcolor: 'background.paperDark' }}
        aria-label="Search events by name"
      />
      {showLifecycleFilter && (
        <Box sx={{ mb: 2 }}>
          <LifecycleFilter
            value={lifecycleFilter}
            onChange={setLifecycleFilter}
            fullWidth
            counts={lifecycleCounts(events)}
          />
        </Box>
      )}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
        role="list"
        aria-label="Events"
      >
        {eventsStatus === 'pending' ? (
          <>
            {[...Array(3)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                sx={{
                  height: 80,
                  mb: 1,
                }}
              />
            ))}
          </>
        ) : filteredEvents?.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'background.paperDark',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No Events Found
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {searchQuery
                ? `No events match "${searchQuery}".`
                : 'No events available.'}
            </Typography>
          </Box>
        ) : (
          filteredEvents?.map((event) => {
            let checked = activeEventId === event.id
            // Handle both Event (thumbnail) and Course (imageName)
            let isEvent = 'hours' in event
            let imageName = isEvent
              ? (event as Event).thumbnail
              : (event as Course).imageName
            let thumbnailUrl = imageName
              ? courseResizedImageSrc(imageName)
              : null

            return (
              <EventCard
                key={event.id}
                event={event}
                checked={checked}
                thumbnailUrl={thumbnailUrl}
                imageName={imageName}
                onSelect={() => onEventSelect(event.id)}
              />
            )
          })
        )}
      </Box>
    </Paper>
  )
}
